// AttachmentStrip — small read-only render of report_submission_attachments
// rows. Used on the Reporting cards (light theme) and inside the
// ReportingModal history list (dark theme). Photos render as 44px
// thumbnails; audio/link as chips. Photos and audio fetch a presigned
// download URL on mount via the storage helper. Clicking a thumbnail opens
// the asset in a new tab — stopPropagation so any parent click handler
// (e.g. the Reporting card's "open modal") doesn't fire.

import { useEffect, useState } from 'react';
import { storage } from './awsStorage';

const SANS = 'Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif';

const THEMES = {
  light: {
    chipBg:   '#FAFAF8',
    chipBorder: '#E8E6E0',
    chipColor:  '#3E4148',
    thumbBg:    '#FAFAF8',
    thumbBorder:'#E8E6E0',
    placeholder:'#EFEDE7',
  },
  dark: {
    chipBg:   'rgba(255,255,255,0.08)',
    chipBorder: 'rgba(255,255,255,0.12)',
    chipColor:  'rgba(255,255,255,0.85)',
    thumbBg:    'rgba(255,255,255,0.06)',
    thumbBorder:'rgba(255,255,255,0.12)',
    placeholder:'rgba(255,255,255,0.10)',
  },
};

function chipStyle(t) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '4px 10px', borderRadius: 999,
    background: t.chipBg, border: `1px solid ${t.chipBorder}`,
    fontFamily: SANS, fontSize: 11, color: t.chipColor,
    textDecoration: 'none', maxWidth: 200, overflow: 'hidden',
    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  };
}
function thumbStyle(t) {
  return {
    width: 44, height: 44, borderRadius: 8,
    border: `1px solid ${t.thumbBorder}`, background: t.thumbBg,
    overflow: 'hidden', display: 'block', textDecoration: 'none',
    flexShrink: 0,
  };
}

function AttachmentItem({ att, theme = 'light' }) {
  const t = THEMES[theme] || THEMES.light;
  const [url, setUrl] = useState(null);
  const isPhoto = att.kind === 'photo';
  const isAudio = att.kind === 'audio';
  const isLink  = att.kind === 'link';

  useEffect(() => {
    if (isLink || !att.path) return;
    let mounted = true;
    storage.from('report-attachments').createSignedUrl(att.path).then(({ data }) => {
      if (mounted && data?.signedUrl) setUrl(data.signedUrl);
    }).catch(() => { /* swallow — chip stays in placeholder state */ });
    return () => { mounted = false; };
  }, [att.path, isLink]);

  if (isLink) {
    return (
      <a href={att.link_url} target="_blank" rel="noopener noreferrer"
         onClick={(e) => e.stopPropagation()}
         title={att.link_url}
         style={chipStyle(t)}>
        🔗 <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{att.label || att.link_url}</span>
      </a>
    );
  }
  if (isPhoto) {
    if (!url) return <span style={{ ...thumbStyle(t), background: t.placeholder }} />;
    return (
      <a href={url} target="_blank" rel="noopener noreferrer"
         onClick={(e) => e.stopPropagation()}
         title={att.label || 'photo'}
         style={thumbStyle(t)}>
        <img src={url} alt={att.label || 'photo'}
             style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </a>
    );
  }
  if (isAudio) {
    return (
      <a href={url || '#'}
         target="_blank" rel="noopener noreferrer"
         onClick={(e) => { e.stopPropagation(); if (!url) e.preventDefault(); }}
         style={chipStyle(t)}>
        🎙️ {att.label || 'voice memo'}
      </a>
    );
  }
  return null;
}

export function AttachmentStrip({ attachments, theme = 'light' }) {
  if (!attachments?.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
      {attachments.map(a => <AttachmentItem key={a.id} att={a} theme={theme} />)}
    </div>
  );
}
